import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import { useInvalidateQueries } from "@/services/utils/useInvalidateQueries"
import type { HttpError } from "@/utils/api"
import { httpClient, toHttpError } from "@/utils/api"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

export interface CreateCredentialInput {
  organizationId: Organization["id"]
  payload: CreateCredentialPayload
}

export type CreateCredentialPayload = {
  notes: string
  password: string
  resourceType: string
  username: string
  website: string
} & (
  | {
      providerName: string
      resourceProviderName?: never
    }
  | {
      providerName?: never
      resourceProviderName: string
    }
)

export interface CreateCredentialResponse {
  credentialId: string
  id?: string
  integrationCredentialId?: string
  resourceProviderId?: string
  resourceProviderName?: string
}

const buildCreateCredentialBody = (payload: CreateCredentialPayload) => {
  const body: Record<string, string> = {
    notes: payload.notes,
    password: payload.password,
    resourceType: payload.resourceType,
    username: payload.username,
    website: payload.website,
  }

  if (payload.resourceProviderName) {
    body.resourceProviderName = payload.resourceProviderName
  } else if (payload.providerName) {
    body.providerName = payload.providerName
  }

  return body
}

export const useCreateCredential = (
  options?: UseMutationOptions<
    CreateCredentialResponse,
    HttpError,
    CreateCredentialInput
  >
) => {
  const { invalidateQueries } = useInvalidateQueries()

  const mutation = useMutation<
    CreateCredentialResponse,
    HttpError,
    CreateCredentialInput
  >({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn: async ({ organizationId, payload }: CreateCredentialInput) => {
      try {
        const response = await httpClient.post<CreateCredentialResponse>(
          `/api/v1/organizations/${organizationId}/pipelines/credentials`,
          buildCreateCredentialBody(payload)
        )

        return response.data
      } catch (error: unknown) {
        throw toHttpError(error)
      }
    },
    onError: (...args) => {
      options?.onError?.(...args)
    },
    onSuccess: (data, ...rest) => {
      // Invalidates credentials list and resource-providers (same service name).
      invalidateQueries([createQueryKey(ApiQueryName.Credentials, "getMany")])

      options?.onSuccess?.(data, ...rest)
    },
  })

  return useRenameQueryResult(mutation, "createCredential")
}
